import { useState } from 'react';
import { ChevronLeft, ChevronRight, FilterX, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SeverityBadge } from './SeverityBadge';
import { FieldDetailModal } from './FieldDetailModal';
import { useAuditFilters } from '../hooks';
import type { AuditDivergencesResponse, AuditSeverity, UnitAudit } from '@claro/shared';
import { AUDIT_SEVERITY_LABELS } from '@claro/shared';
import type { AuditValidityFilter } from '../hooks/useAuditFilters';

interface DivergenceTableProps {
  data: AuditDivergencesResponse | undefined;
  isLoading: boolean;
}

const ALL_SEVERITIES: AuditSeverity[] = [0, 1, 2, 3, 4, 5];

const FIELD_LABELS: Record<string, string> = {
  logradouro: 'Logradouro',
  numero: 'Número',
  complemento: 'Complemento',
  bairro: 'Bairro',
  cep: 'CEP',
  municipio: 'Município',
  uf: 'UF',
  inscricaoEstadual: 'Inscrição Estadual',
  inscricaoMunicipal: 'Inscrição Municipal',
};

export function DivergenceTable({ data, isLoading }: DivergenceTableProps) {
  const {
    severities,
    field,
    regional,
    search,
    alvaraValidity,
    avcbValidity,
    page,
    toggleSeverity,
    setSeverities,
    setField,
    setRegional,
    setSearch,
    setAlvaraValidity,
    setAvcbValidity,
    setPage,
    resetFilters,
  } = useAuditFilters();
  const [selectedUnit, setSelectedUnit] = useState<UnitAudit | null>(null);
  const hasActiveFilters = severities.length > 0 || !!field || !!regional || !!search || !!alvaraValidity || !!avcbValidity;

  return (
    <div className="rounded-lg border bg-card">
      {/* Toolbar */}
      <div className="space-y-3 border-b p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Buscar CNPJ, unidade..."
            className="h-8 w-64"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex flex-wrap gap-1">
            {ALL_SEVERITIES.map((s) => (
              <button
                key={s}
                className={`rounded-md border px-2 py-0.5 text-xs font-medium transition ${
                  severities.includes(s)
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-muted text-muted-foreground hover:bg-muted/50'
                }`}
                onClick={() => toggleSeverity(s)}
              >
                {s} · {AUDIT_SEVERITY_LABELS[s]}
              </button>
            ))}
          </div>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={resetFilters}>
              <FilterX className="h-3.5 w-3.5" />
              Limpar filtros
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <ValidityFilterGroup label="Alvará efetivamente válido" value={alvaraValidity} onChange={setAlvaraValidity} />
          <ValidityFilterGroup label="AVCB válido" value={avcbValidity} onChange={setAvcbValidity} />
        </div>

        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-medium text-muted-foreground">Filtros ativos</span>
            {severities.length > 0 && (
              <button
                className="inline-flex h-7 items-center gap-1 rounded-md border border-primary/30 bg-primary/10 px-2 font-medium text-primary transition hover:bg-primary/15"
                onClick={() => setSeverities([])}
              >
                Severidade: {severities.map(s => `${s}`).join(', ')}
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {field && (
              <button
                className="inline-flex h-7 items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 font-medium text-amber-700 transition hover:bg-amber-500/15 dark:text-amber-300"
                onClick={() => setField(null)}
              >
                Campo: {FIELD_LABELS[field] ?? field}
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {regional && (
              <button
                className="inline-flex h-7 items-center gap-1 rounded-md border border-blue-500/30 bg-blue-500/10 px-2 font-medium text-blue-700 transition hover:bg-blue-500/15 dark:text-blue-300"
                onClick={() => setRegional(null)}
              >
                Regional: {regional}
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {search && (
              <button
                className="inline-flex h-7 items-center gap-1 rounded-md border bg-muted px-2 font-medium text-muted-foreground transition hover:bg-muted/70"
                onClick={() => setSearch('')}
              >
                Busca: {search}
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {alvaraValidity && (
              <button
                className="inline-flex h-7 items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 font-medium text-emerald-700 transition hover:bg-emerald-500/15 dark:text-emerald-300"
                onClick={() => setAlvaraValidity(null)}
              >
                Alvará efetivo: {alvaraValidity === 'valid' ? 'válidos' : 'não válidos'}
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {avcbValidity && (
              <button
                className="inline-flex h-7 items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 font-medium text-emerald-700 transition hover:bg-emerald-500/15 dark:text-emerald-300"
                onClick={() => setAvcbValidity(null)}
              >
                AVCB: {avcbValidity === 'valid' ? 'válidos' : 'não válidos'}
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Table body */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !data || !data.data || data.data.length === 0 ? (
        <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
          Nenhuma divergência encontrada
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="px-3 py-2">Unidade</th>
                <th className="px-3 py-2">CNPJ</th>
                <th className="px-3 py-2">Regional</th>
                <th className="px-3 py-2 text-center">Campos</th>
                <th className="px-3 py-2 text-center">Alvará efetivo</th>
                <th className="px-3 py-2 text-center">AVCB</th>
                <th className="px-3 py-2 text-center">Severidade</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((unit) => (
                <tr
                  key={`${unit.unitGuid ?? unit.unitId}:${unit.cnpj}`}
                  className="cursor-pointer border-b transition hover:bg-muted/50"
                  onClick={() => setSelectedUnit(unit)}
                >
                  <td className="px-3 py-2 font-medium">
                    {unit.unitName || unit.unitId}
                    {unit.identificacaoTax && (
                      <span className="ml-1 text-xs text-muted-foreground">({unit.identificacaoTax})</span>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-xs">
                    {unit.cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}
                  </td>
                  <td className="px-3 py-2 text-xs">{unit.regional || '—'}</td>
                  <td className="px-3 py-2 text-center text-xs">
                    <span className="font-bold text-red-500">{unit.divergingFields}</span>
                    <span className="text-muted-foreground">/{unit.totalFields}</span>
                  </td>
                  <td className="px-3 py-2 text-center"><DocumentValidityBadge state={unit.documentValidity?.alvaraEffective} /></td>
                  <td className="px-3 py-2 text-center"><DocumentValidityBadge state={unit.documentValidity?.avcb} /></td>
                  <td className="px-3 py-2 text-center">
                    <SeverityBadge severity={unit.overallSeverity} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
          <span>
            {data.total} vínculos unidade/CNPJ · Página {data.page} de {data.totalPages}
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={page >= data.totalPages}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      <FieldDetailModal unit={selectedUnit} onClose={() => setSelectedUnit(null)} />
    </div>
  );
}

function ValidityFilterGroup({ label, value, onChange }: { label: string; value: AuditValidityFilter; onChange: (value: AuditValidityFilter) => void }) {
  return <div className="flex items-center gap-1 rounded-md border bg-muted/20 p-1 text-xs">
    <span className="px-1.5 font-medium text-muted-foreground">{label}</span>
    <button
      type="button"
      className={`rounded px-2 py-1 transition ${value === 'valid' ? 'bg-emerald-500/15 font-medium text-emerald-700 dark:text-emerald-300' : 'hover:bg-muted'}`}
      onClick={() => onChange(value === 'valid' ? null : 'valid')}
    >Válidos</button>
    <button
      type="button"
      className={`rounded px-2 py-1 transition ${value === 'invalid' ? 'bg-amber-500/15 font-medium text-amber-700 dark:text-amber-300' : 'hover:bg-muted'}`}
      onClick={() => onChange(value === 'invalid' ? null : 'invalid')}
    >Não válidos</button>
  </div>;
}

function DocumentValidityBadge({ state }: { state?: 'valid' | 'expired' | 'unknown' | 'wrong-cnpj' | 'blocked-by-avcb' }) {
  const label = state === 'valid' ? 'Válido'
    : state === 'expired' ? 'Vencido'
    : state === 'wrong-cnpj' ? 'CNPJ divergente'
    : state === 'blocked-by-avcb' ? 'Bloqueado pelo AVCB'
    : 'Não confirmado';
  const tone = state === 'valid'
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
    : state === 'unknown' || !state
      ? 'border-muted bg-muted/40 text-muted-foreground'
      : 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300';
  return <span className={`inline-flex whitespace-nowrap rounded-md border px-2 py-0.5 text-[10px] font-medium ${tone}`}>{label}</span>;
}
